import { z, ZodFormattedError } from "zod";
import { PostTypeEnum } from "~/types/asgard/PostType";

const baseSchema = z.object({
  acfFcLayout: z.string(),
});

export enum SidecarBlockLayout {
  KeyTopics = "keytopics",
  NewsletterCTA = "newsletter_cta",
  Piano = "piano",
  RelatedReport = "related_report",
  PostListContext = "content_list_auto_context",
  PostListFiltered = "content_list_auto_filtered",
  PostListCurated = "content_list_curated",
  Wysiwyg = "wysiwyg",

  InvalidProperties = "invalid_properties",
  UnknownBlock = "unknown_block",
}

export const KeyTopicsBlockSchema = baseSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.KeyTopics),
  topics: z.array(
    z.object({
      name: z.string(),
      slug: z.string(),
    })
  ),
});
export type KeyTopicsBlock = z.infer<typeof KeyTopicsBlockSchema>;
export const isKeyTopicsBlock = (
  block: SidecarBlock
): block is KeyTopicsBlock =>
  block.acfFcLayout === SidecarBlockLayout.KeyTopics;

export const NewsletterBlockSchema = baseSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.NewsletterCTA),
});
export type NewsletterBlock = z.infer<typeof NewsletterBlockSchema>;
export const isNewsletterBlock = (
  block: SidecarBlock
): block is NewsletterBlock =>
  block.acfFcLayout === SidecarBlockLayout.NewsletterCTA;

export const PianoBlockSchema = baseSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.Piano),
  targetId: z.string(),
});
export type PianoBlock = z.infer<typeof PianoBlockSchema>;
export const isPianoBlock = (block: SidecarBlock): block is PianoBlock =>
  block.acfFcLayout === SidecarBlockLayout.Piano;

export const PostSummarySchema = z.object({
  image: z
    .object({
      source: z.string(),
      altText: z.string().nullable().optional(),
    })
    .optional()
    .nullable(),
  primaryCategory: z
    .object({
      termId: z.number(),
      name: z.string(),
      slug: z.string(),
    })
    .optional()
    .nullable(),
  primaryTag: z
    .object({
      termId: z.number(),
      name: z.string(),
      slug: z.string(),
    })
    .optional()
    .nullable(),
  title: z.string(),
  slug: z.string(),
  type: z.nativeEnum(PostTypeEnum),
  dateGmt: z.string().nullable().optional(),
  subhead: z.string().nullable().optional(),
});

const ContentSummarySchema = z.object({
  bulletImage: z.string().nullable().optional(),
  content: PostSummarySchema,
});

export const RelatedReportSchema = baseSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.RelatedReport),
  attachmentId: z.number(),
  postId: z.number(),
  dataAccess: z.array(z.string()),
  wysiwyg: z.string(),
});
export type RelatedReportBlock = z.infer<typeof RelatedReportSchema>;
export const isRelatedReportBlock = (
  block: SidecarBlock
): block is RelatedReportBlock =>
  block.acfFcLayout === SidecarBlockLayout.RelatedReport;

export const PostListSchema = baseSchema.extend({
  acfFcLayout: z.union([
    z.literal(SidecarBlockLayout.PostListContext),
    z.literal(SidecarBlockLayout.PostListCurated),
    z.literal(SidecarBlockLayout.PostListFiltered),
  ]),
  title: z.string(),
  relatedContent: z.array(ContentSummarySchema),
  optionalCta: z
    .object({
      title: z.string(),
      url: z.string(),
    })
    .optional()
    .nullable(),
});

const PostListAutoContextSchema = PostListSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.PostListContext),
});
const PostListAutoFilteredSchema = PostListSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.PostListFiltered),
});
const PostListCuratedSchema = PostListSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.PostListCurated),
});
export type PostListAutoContextBlock = z.infer<
  typeof PostListAutoContextSchema
>;
export type PostListAutoFilteredBlock = z.infer<
  typeof PostListAutoFilteredSchema
>;
export type PostListCuratedBlock = z.infer<typeof PostListCuratedSchema>;

export type PostListBlock =
  | PostListAutoContextBlock
  | PostListAutoFilteredBlock
  | PostListCuratedBlock;

export const isPostListBlock = (block: SidecarBlock): block is PostListBlock =>
  [
    SidecarBlockLayout.PostListContext as string,
    SidecarBlockLayout.PostListFiltered as string,
    SidecarBlockLayout.PostListCurated as string,
  ].includes(block.acfFcLayout);

export const WysiwygBlockSchema = baseSchema.extend({
  acfFcLayout: z.literal(SidecarBlockLayout.Wysiwyg),
  wysiwyg: z.string(),
  optionalImage: z.string().optional().nullable(),
  optionalImageLink: z
    .object({
      url: z.string(),
    })
    .optional()
    .nullable(),
  optionalCta: z
    .object({
      title: z.string(),
      url: z.string(),
    })
    .optional()
    .nullable(),
  optionalCtaStyle: z
    .union([z.literal("text_link"), z.literal("button")])
    .default("text_link")
    .optional()
    .nullable(),
  optionalImageOrientation: z
    .union([z.literal("4/3"), z.literal("3/4")])
    .nullable()
    .optional(),
  backgroundColor: z.string().optional().nullable(),
});
export type WysiwygBlock = z.infer<typeof WysiwygBlockSchema>;
export const isWysiwygBlock = (block: SidecarBlock): block is WysiwygBlock =>
  block.acfFcLayout === SidecarBlockLayout.Wysiwyg;

export type InvalidPropertiesBlock = {
  acfFcLayout: SidecarBlockLayout.InvalidProperties;
  blockName: string;
  error: ZodFormattedError<unknown>;
};

export type UnknownBlock = {
  acfFcLayout: SidecarBlockLayout.UnknownBlock;
  block: unknown;
};

export type SidecarBlock =
  | KeyTopicsBlock
  | NewsletterBlock
  | PianoBlock
  | PostListBlock
  | RelatedReportBlock
  | WysiwygBlock
  | InvalidPropertiesBlock
  | UnknownBlock;
